import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { WorkflowDef } from "@/gen/omnios/v1/accounting_pb";
import { useListWorkflows, useRunWorkflow } from "@/hooks/use-accounting";
import { useAccountingContext } from "@/lib/accounting-context";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";

import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Play, Search, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/accounting/operations")({
	component: OperationsPage,
});

const STATUS_CONFIG: Record<
	string,
	{ icon: typeof CheckCircle2; color: string; label: string }
> = {
	completed: {
		icon: CheckCircle2,
		color: "text-positive",
		label: "Выполнено",
	},
	running: { icon: Loader2, color: "text-blue-500", label: "Выполняется" },
	failed: { icon: XCircle, color: "text-destructive", label: "Ошибка" },
	enqueued: { icon: Loader2, color: "text-muted-foreground", label: "В очереди" },
};

function OperationsPage() {
	const { t } = useTranslation("accounting");
	const { periodId } = useAccountingContext();
	const { data, isLoading } = useListWorkflows(periodId);
	const runWorkflow = useRunWorkflow();

	useBreadcrumbs([
		{
			label: t("regulatory_operations", {
				defaultValue: "Регламентные операции",
			}),
		},
	]);

	const [runningId, setRunningId] = useState<string | null>(null);

	const workflows = data?.workflows ?? [];

	const handleRun = (name: string, dryRun: boolean) => {
		setRunningId(name);
		runWorkflow.mutate(
			{ workflowName: name, periodId, dryRun },
			{
				onSuccess: (res) => {
					toast.success(
						dryRun
							? t("workflow_dry_run_done", {
									defaultValue: "Пробный запуск завершён",
								})
							: t("workflow_run_done", {
									defaultValue: `Операция выполнена. Создано проводок: ${res.entriesCreated}`,
								}),
					);
					setRunningId(null);
				},
				onError: () => {
					setRunningId(null);
				},
			},
		);
	};

	const [searchText, setSearchText] = useState("");

	const filteredWorkflows = useMemo(
		() =>
			workflows.filter((wf) =>
				wf.displayName.toLowerCase().includes(searchText.toLowerCase()) ||
				wf.description.toLowerCase().includes(searchText.toLowerCase()),
			),
		[workflows, searchText],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			},
			{
				id: "count",
				content: `${workflows.length} ${t("operations_count", { defaultValue: "операций" })}`,
				align: "left" as const,
			},
		],
		[periodId, workflows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					size="sm"
					className="h-9"
					onClick={() => {
						for (const wf of workflows) {
							handleRun(wf.name, false);
						}
					}}
					disabled={runWorkflow.isPending}
				>
					{t("run_all_operations", { defaultValue: "Запустить все" })}
				</Button>
			</div>
			<div className="flex-1 overflow-auto">
			<div className="divide-y">
				{filteredWorkflows.map((wf) => (
					<WorkflowCard
						key={wf.name}
						workflow={wf}
						isRunning={runningId === wf.name}
						onRun={() => handleRun(wf.name, false)}
						onDryRun={() => handleRun(wf.name, true)}
						t={t}
					/>
				))}
			</div>

			{filteredWorkflows.length === 0 && (
				<div className="flex-1 flex items-center justify-center">
					<p className="text-sm text-muted-foreground">
						{t("no_workflows", {
							defaultValue: "Нет доступных регламентных операций",
						})}
					</p>
				</div>
			)}
			</div>
		</div>
	);
}

function WorkflowCard({
	workflow,
	isRunning,
	onRun,
	onDryRun,
	t,
}: {
	workflow: WorkflowDef;
	isRunning: boolean;
	onRun: () => void;
	onDryRun: () => void;
	t: (key: string, opts?: Record<string, unknown>) => string;
}) {
	const status = STATUS_CONFIG[workflow.lastRunStatus];
	const StatusIcon = status?.icon;

	return (
		<div className="flex items-center gap-4 px-4 py-3 hover:bg-accent/30 transition-colors">
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium">{workflow.displayName}</p>
				<p className="text-xs text-muted-foreground mt-0.5">
					{workflow.description}
				</p>
			</div>

			{/* Last run status */}
			{workflow.lastRunStatus && status && (
				<div className="flex items-center gap-1.5 text-xs shrink-0">
					{StatusIcon && (
						<StatusIcon
							className={`h-3.5 w-3.5 ${status.color} ${workflow.lastRunStatus === "running" ? "animate-spin" : ""}`}
						/>
					)}
					<span className={status.color}>{status.label}</span>
					{workflow.lastEntriesCreated > 0 && (
						<Badge variant="outline" className="text-[10px] ml-1">
							{workflow.lastEntriesCreated}{" "}
							{t("entries_short", { defaultValue: "пр." })}
						</Badge>
					)}
				</div>
			)}

			{/* Actions */}
			<div className="flex items-center gap-1 shrink-0">
				<Button
					variant="outline"
					size="sm"
					onClick={onDryRun}
					disabled={isRunning}
					className="gap-1 text-xs"
				>
					{t("dry_run", { defaultValue: "Пробный" })}
				</Button>
				<Button
					size="sm"
					onClick={onRun}
					disabled={isRunning}
					className="gap-1 text-xs"
				>
					{isRunning ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<Play className="h-3 w-3" />
					)}
					{t("run", { defaultValue: "Запустить" })}
				</Button>
			</div>
		</div>
	);
}
